import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import SubmissionQueue from './components/SubmissionQueue';
import DetailPanel from './components/DetailPanel';
import Login from './components/Login';
import { supabase } from './lib/supabase';
import { DEMO_DATA } from './lib/demo';
import { OPEN_STATUSES } from './lib/utils';

const API       = process.env.REACT_APP_API_URL || 'https://api.cerebrorisk.com';
const TENANT_ID = '0b81d0d9-bbd2-4cae-a814-345399f60e1b';
const API_KEY   = 'af4d4829d5bfbc9353dd7371b05a3c69c8cbe10fb61c5b383963b22233cebab2';


export default function App() {
  const [session,     setSession]     = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState('all');
  const [search,      setSearch]      = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const loadSubmissions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/submissions?tenant_id=${TENANT_ID}`, {
        headers: { 'x-api-key': API_KEY },
      });
      if (res.ok) {
        const data = await res.json();
        const list = data?.length ? data : DEMO_DATA;
        setSubmissions(list);
        setSelected(prev => {
          if (!prev) return null;
          const updated = list.find(s => s.id === prev.id);
          return updated || prev;
        });
      } else {
        setSubmissions(DEMO_DATA);
      }
    } catch {
      setSubmissions(DEMO_DATA);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session) loadSubmissions();
  }, [session, loadSubmissions]);

  if (authLoading) return (
    <div style={{
      height:'100vh', display:'flex', alignItems:'center',
      justifyContent:'center', background:'var(--bg)',
      color:'var(--muted)', fontSize:12,
    }}>
      Loading...
    </div>
  );

  if (!session) return <Login />;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const stats = {
    open  : submissions.filter(s => OPEN_STATUSES.includes(s.status)).length,
    sla   : submissions.filter(s => s.sla_due_at && new Date(s.sla_due_at) < new Date()
                                    && OPEN_STATUSES.includes(s.status)).length,
    quoted: submissions.filter(s => {
      if (!['quote_sent', 'ready_to_bind', 'policy_issuance'].includes(s.status)) return false;
      const t = new Date(s.updated_at || s.received_at);
      return t >= today;
    }).length,
  };


  return (
    <div style={{
      display:'grid',
      gridTemplateColumns:'220px 1fr',
      gridTemplateRows:'56px 1fr',
      height:'100vh',
      overflow:'hidden',
    }}>
      <Topbar stats={stats} onRefresh={loadSubmissions} session={session} />
      <Sidebar
        submissions={submissions}
        filter={filter}
        setFilter={setFilter}
      />
      <main style={{
        overflow:'auto', display:'flex',
        flexDirection:'column', background:'var(--bg)',
      }}>
        <SubmissionQueue
          submissions={submissions}
          loading={loading}
          filter={filter}
          setFilter={setFilter}
          search={search}
          setSearch={setSearch}
          selected={selected}
          onSelect={setSelected}
          onRefresh={loadSubmissions}
        />
      </main>
      {selected && (
        <DetailPanel
          submission={selected}
          onClose={() => setSelected(null)}
          onRefresh={loadSubmissions}
        />
      )}
    </div>
  );
}
